import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useFormikContext } from 'formik';
import { type ReactElement, useMemo } from 'react';

import type { QuoteFormikType } from '@core/formik/quote';
import type { ProjectProduct } from '@core/types/api/ProjectProduct/ProjectProduct';
import { hasValue } from '@core/utils/equality';
import { LoadingSpinner } from '@web/components/LoadingSpinner/LoadingSpinner';
import { useSlabQuery } from '@web/hooks/useSlabQuery';

import { isQuoteConfigSynced } from '../utils';
import ProductsByPlantSection from './ProductsByPlantSection';
import type { ProductsSectionProps } from './ProductsSection';

export const AdditionalServicesSection = ({
  curProductsRef,
}: ProductsSectionProps): ReactElement => {
  const formikBag = useFormikContext<QuoteFormikType>();

  const quoteConfigId = formikBag.values.quoteConfig?.id;
  const {
    isLoading: isLoadingQuoteConfig,
    isError: isErrorQuoteConfig,
    data: curQuoteConfig,
  } = useSlabQuery(
    'GET quote config by ID',
    { pathParams: { id: quoteConfigId ?? '' } },
    { enabled: hasValue(quoteConfigId) },
  );

  const {
    data: project,
    isLoading: isLoadingProject,
    isError: isErrorProject,
  } = useSlabQuery(
    'GET project by ID',
    {
      pathParams: {
        id: formikBag.values.project.id ?? '',
      },
    },
    {
      enabled: formikBag.values.project.id !== null,
    },
  );

  const {
    isLoading: isLoadingProjectProducts,
    isError: isErrorProjectProducts,
    data: projectProducts,
  } = useSlabQuery(
    'GET project products by project ID',
    {
      pathParams: {
        id: formikBag.values.project.id ?? '',
      },
    },
    {
      enabled: formikBag.values.project.id !== null,
    },
  );

  const projectProductsByProductID = useMemo(() => {
    if (formikBag.values.project.id === null) {
      return {};
    }
    const byProductID: Record<string, ProjectProduct> = {};
    projectProducts?.forEach((pp) => {
      byProductID[pp.product.id] = pp;
    });
    return byProductID;
  }, [projectProducts]);

  if (formikBag.values.project.id === null) {
    return (
      <Box>
        <Typography variant='h5'>Please select a project.</Typography>
      </Box>
    );
  }

  const isLoading = isLoadingQuoteConfig || isLoadingProject || isLoadingProjectProducts;
  if (isLoading) {
    return <LoadingSpinner />;
  }

  const isError = isErrorQuoteConfig || isErrorProject || isErrorProjectProducts;
  const dataUndefined = project === undefined || projectProducts === undefined;
  if (isError || dataUndefined) {
    return (
      <Box>
        <Typography variant='h5'>Error.</Typography>
      </Box>
    );
  }

  const { areProductsSynced } = isQuoteConfigSynced(
    curQuoteConfig,
    formikBag.values.additionalProductsByPlants,
    formikBag.values.terms,
  );
  const tip = ((): ReactElement | null => {
    if (areProductsSynced) {
      return null;
    }
    return (
      <Box pb='0.5rem'>
        <Typography variant='body3'>
          Current additional services are out-of-sync with selected quote configuration.
        </Typography>
      </Box>
    );
  })();

  return (
    <>
      {tip}
      <ProductsByPlantSection
        sectionType='additional'
        curProductsRef={curProductsRef}
        project={project}
        projectProductsByProductID={projectProductsByProductID}
      />
    </>
  );
};
